// src/content/labPages.ts
//
// Single source of truth for the public Labs landing pages.
// The page UI AND all JSON-LD must be generated from this file (see buildLabJsonLd at the bottom),
// so visible copy and structured data can never drift apart.
//
// CONFIRM before publishing:
//  [1] Plan access for the labs (free vs Explorer+). Copy is intentionally silent on pricing/gating.
//  [2] Whether the Code Lab trace runs code in a sandbox or is AI-simulated. Copy assumes AI-simulated
//      ("AI-guided walkthrough"). If it really executes code, update the code FAQ "Does the Code Lab run my code?"
//      and the code quickAnswer, and say so loudly: it is a real differentiator.

export type LabKey = "hub" | "math" | "code" | "bio";

export interface LabFaq {
  q: string;
  a: string;
}
export interface LabStep {
  name: string;
  text: string;
}
export interface LabFeature {
  title: string;
  body: string;
  path?: string;
}
export interface LabExampleBlock {
  label: string;
  code?: string; // render in a mono code block
  text?: string; // render as paragraph
}
export interface LabExample {
  title: string;
  intro: string;
  blocks: LabExampleBlock[];
  table?: { head: string[]; rows: string[][] };
  takeaway: string;
}
export interface LabChooserRow {
  need: string;
  lab: string;
  path: string;
}

export interface LabPage {
  key: LabKey;
  path: string; // public marketing route (NOT under /app)
  crumbName: string; // breadcrumb label
  title: string; // <title>, <= 60 chars incl. brand suffix
  description: string; // meta description, <= 155 chars
  h1: string;
  subhead: string;
  ctaLabel: string;
  ctaPath: string;
  quickAnswer: string; // AEO answer box, rendered directly under the hero (id="quick-answer")
  howToName: string; // used for HowTo schema + the "How it works" heading
  steps: LabStep[]; // empty => no HowTo section / schema
  featuresHeading: string;
  features: LabFeature[];
  example?: LabExample;
  chooser?: LabChooserRow[];
  faqs: LabFaq[];
  related: { label: string; path: string }[];
  targetQueries: string[]; // work these into headings/body naturally; do not keyword-stuff
  dateModified: string; // show as "Last updated" on the page
}

export const SITE = "https://learnex.cc";

export const LAB_PAGES: Record<LabKey, LabPage> = {
  // ---------------------------------------------------------------- HUB
  hub: {
    key: "hub",
    path: "/labs",
    crumbName: "Labs",
    title: "Problem Labs for Math, Code & Biology | Learnex",
    description:
      "Three interactive labs: a math mistake finder, a code debugging tutor and a biology pathway simulator. Practice the reasoning exams actually test.",
    h1: "Problem labs for math, code and biology",
    subhead:
      "Study tools that respond to what you actually wrote, ran or predicted, not just the topic you picked.",
    ctaLabel: "Start free",
    ctaPath: "/signup",
    quickAnswer:
      "Learnex Labs are three interactive practice tools inside Learnex: a Math Lab that finds the first wrong step in your working, a Code Lab that pinpoints bugs and traces execution, and a Bio Lab that simulates what breaks when a biological pathway is blocked. Each one teaches the reasoning instead of just handing over an answer.",
    howToName: "",
    steps: [],
    featuresHeading: "Three labs, one idea: learn the reasoning",
    features: [
      {
        title: "Math Lab",
        body: "Enter your working line by line. Learnex flags the first wrong step, names the error and shows the fix. Stuck before you start? The Derivation Co-Pilot guides you without giving away the answer.",
        path: "/math-mistake-finder",
      },
      {
        title: "Code Lab",
        body: "Paste your code. Get the buggy line, a failing test case, and a step-by-step trace with a live memory table. Then practise the fix with Parsons-style challenges.",
        path: "/code-debugging-tutor",
      },
      {
        title: "Bio Lab",
        body: "Pick a drug, toxin or mutation. See what builds up before the block, what runs dry after it, and how the body compensates.",
        path: "/biology-pathway-simulator",
      },
    ],
    chooser: [
      { need: "My answer is wrong and I can't see why", lab: "Math Lab: Find My Mistake", path: "/math-mistake-finder" },
      { need: "I don't know where to start on a problem", lab: "Math Lab: Derivation Co-Pilot", path: "/math-mistake-finder" },
      { need: "I know the formula but not why it works", lab: "Math Lab: Formula Intuition", path: "/math-mistake-finder" },
      { need: "My code crashes or returns the wrong output", lab: "Code Lab: Find My Bug", path: "/code-debugging-tutor" },
      { need: "I can't follow what this algorithm does", lab: "Code Lab: Execution Trace", path: "/code-debugging-tutor" },
      { need: "What happens if this enzyme, drug or mutation blocks a step?", lab: "Bio Lab: What Breaks If...?", path: "/biology-pathway-simulator" },
      { need: "I need the order of events in a pathway", lab: "Bio Lab: Causal Pathway", path: "/biology-pathway-simulator" },
    ],
    faqs: [
      {
        q: "What are Learnex Labs?",
        a: "Learnex Labs are interactive practice tools for subjects where understanding comes from doing: a Math Lab, a Code Lab and a Bio Lab. Each one reacts to your own input, such as your working, your code or the perturbation you choose, and explains the reasoning behind what it finds.",
      },
      {
        q: "Do I need to upload course material to use a lab?",
        a: "No. You can use each lab on its own by typing or pasting a problem, a piece of code or a biological scenario. Uploading your notes is still useful for the rest of Learnex, such as quizzes, flashcards and your learning trail.",
      },
      {
        q: "Which lab should I use?",
        a: "Use the Math Lab when a calculation goes wrong, the Code Lab when a program fails or you can't follow an algorithm, and the Bio Lab when you need to predict the effects of blocking a pathway. The table on this page matches common situations to the right lab.",
      },
      {
        q: "Do the labs just give me answers?",
        a: "No, they are built to teach the reasoning. The Math Lab's co-pilot guides you step by step without handing over the final answer, the Code Lab asks you to predict what happens next, and the Bio Lab ends with a challenge question on the causal chain.",
      },
      {
        q: "Can the labs make mistakes?",
        a: "Yes. Learnex is AI-powered and AI can be wrong, especially on long or unusual problems. Treat the labs as study aids and check important results against your textbook, notes or instructor.",
      },
    ],
    related: [
      { label: "Math Mistake Finder", path: "/math-mistake-finder" },
      { label: "Code Debugging Tutor", path: "/code-debugging-tutor" },
      { label: "Biology Pathway Simulator", path: "/biology-pathway-simulator" },
      { label: "Socratic AI Tutor", path: "/socratic-tutor" },
      { label: "Exam Study Planner", path: "/study-planner" },
    ],
    targetQueries: [
      "ai study labs",
      "math code biology practice tools",
      "interactive problem solving practice for students",
    ],
    dateModified: "2026-11-04",
  },

  // ---------------------------------------------------------------- MATH
  math: {
    key: "math",
    path: "/math-mistake-finder",
    crumbName: "Math Mistake Finder",
    title: "Math Mistake Finder: Find Your Wrong Step | Learnex",
    description:
      "Enter your working line by line. Learnex finds the first wrong step, names the error and shows the fix side by side.",
    h1: "Find the exact line where your math went wrong",
    subhead:
      "Enter your working line by line. Learnex checks each step, flags the first wrong one and shows the fix, so you learn the rule, not just the answer.",
    ctaLabel: "Start free",
    ctaPath: "/signup",
    quickAnswer:
      "To find a mistake in your math working, check each line against the one before it and stop at the first line that does not follow. That line holds the error. The Learnex Math Lab does this for you: enter your working one step per line and it flags the first faulty line, names the error type, such as a sign flip or a missed chain-rule factor, and shows the corrected line side by side.",
    howToName: "How to find a mistake in your math working with Learnex",
    steps: [
      {
        name: "Enter the problem and your working",
        text: "Type or paste the problem, then write your solution one step per line. Use the symbol bar for powers, fractions, roots and integrals.",
      },
      {
        name: "Run the check",
        text: "Learnex goes through your working line by line and tests whether each step follows from the one before it.",
      },
      {
        name: "Read the diagnosis",
        text: "It flags the first faulty line, names the type of error and explains the rule that was missed.",
      },
      {
        name: "Compare and redo",
        text: "See your line and the corrected line side by side, then redo the working from that point yourself.",
      },
    ],
    featuresHeading: "More than a right-or-wrong check",
    features: [
      {
        title: "Exact-line diagnosis",
        body: "Not just \"wrong answer\": the specific line where your working stops following from the last one.",
      },
      {
        title: "Named error types",
        body: "Distributive property misses, sign flips, missed chain-rule factors and fraction-splitting errors, each explained in plain words.",
      },
      {
        title: "Side-by-side fix",
        body: "Your line next to the corrected line, with the rule behind the correction.",
      },
      {
        title: "Derivation Co-Pilot",
        body: "Stuck before you start? Get guided through strategy, rule, substitution and a sanity check without being handed the final answer.",
      },
      {
        title: "Formula Intuition",
        body: "See what each symbol does and the geometric or physical picture behind a formula.",
      },
      {
        title: "Built for real coursework",
        body: "Calculus, linear algebra, physics-style mechanics and chemistry stoichiometry problems.",
      },
    ],
    example: {
      title: "Example: a missed chain-rule factor",
      intro: "A student differentiates y = sin(3x²). Here is the working and what the lab reports.",
      blocks: [
        {
          label: "Student working",
          code: "Line 1:  y = sin(3x²)\nLine 2:  y′ = cos(3x²)        <- first wrong line",
        },
        {
          label: "Diagnosis",
          text: "Missed chain-rule factor. The derivative of the inside function 3x² is 6x, and it has to multiply the result.",
        },
        {
          label: "Corrected line",
          code: "y′ = 6x · cos(3x²)",
        },
      ],
      takeaway:
        "When you differentiate a function of a function, differentiate the outside, keep the inside, then multiply by the derivative of the inside.",
    },
    faqs: [
      {
        q: "How do I find a mistake in my math working?",
        a: "Check each line against the one before it and stop at the first line that does not logically follow. That line contains the error. The Learnex Math Lab automates this: enter your working one step per line and it flags the first faulty line, names the error type and shows a corrected version side by side.",
      },
      {
        q: "Does the Math Lab just give me the final answer?",
        a: "No. The Derivation Co-Pilot guides you through strategy, rule, substitution and a sanity check without handing over the final answer. The mistake finder shows the corrected line and the way forward from the point of error, so you can redo the rest yourself.",
      },
      {
        q: "What kinds of mistakes can it catch?",
        a: "Common algebra and calculus slips, such as distributive property misses, sign flips, missed chain-rule factors and fraction-splitting errors. Each error is named and explained so you can spot the same trap next time.",
      },
      {
        q: "How is this different from a math solver?",
        a: "A solver starts from the problem and shows its own solution. The mistake finder starts from your working and responds to what you actually wrote, which is what tells you why you went wrong.",
      },
      {
        q: "Which subjects does it cover?",
        a: "Calculus, linear algebra, physics-style mechanics and chemistry stoichiometry problems. A symbol bar helps you enter powers, fractions, roots and integrals on desktop or mobile.",
      },
      {
        q: "Can the Math Lab be wrong?",
        a: "Yes. Learnex is AI-powered and AI can make mistakes, especially in long multi-step working. Check the flagged line against your textbook or notes. The lab explains the rule it applied so you can verify it yourself.",
      },
    ],
    related: [
      { label: "All Learnex Labs", path: "/labs" },
      { label: "Code Debugging Tutor", path: "/code-debugging-tutor" },
      { label: "Biology Pathway Simulator", path: "/biology-pathway-simulator" },
      { label: "Socratic AI Tutor", path: "/socratic-tutor" },
    ],
    targetQueries: [
      "find my mistake in math",
      "check my math working step by step",
      "where did I go wrong in my calculation",
      "math error checker for students",
    ],
    dateModified: "2026-11-04",
  },

  // ---------------------------------------------------------------- CODE
  code: {
    key: "code",
    path: "/code-debugging-tutor",
    crumbName: "Code Debugging Tutor",
    title: "Code Debugging Tutor: Find the Bug & Trace It | Learnex",
    description:
      "Paste your code. Learnex pinpoints the buggy line, shows a failing test case and traces execution step by step. Python, JS, Java, C++, Go, Rust.",
    h1: "Find the bug. Then see why it breaks.",
    subhead:
      "Paste your code. Learnex pinpoints the faulty line, shows a failing test case and walks through execution step by step, so you can see what the program is really doing.",
    ctaLabel: "Start free",
    ctaPath: "/signup",
    quickAnswer:
      "To debug code, reproduce the failure with a concrete input, then trace the variable values line by line until you find the first point where the actual state differs from what you expected. The Learnex Code Lab guides you through both parts: it pinpoints the faulty line, shows a failing test case (input, expected output, actual output) and walks execution step by step with a live memory table.",
    howToName: "How to debug your code with the Learnex Code Lab",
    steps: [
      {
        name: "Paste your code and pick the language",
        text: "Python, JavaScript or TypeScript, Java, C++, Go and Rust are supported.",
      },
      {
        name: "Run Find My Bug",
        text: "Learnex pinpoints the faulty line, names the type of defect and gives a failing test case: input, expected output and actual output.",
      },
      {
        name: "Trace the execution",
        text: "Step through the algorithm on a concrete input and watch variables and the call stack change. At key branches you are asked to predict what happens next.",
      },
      {
        name: "Practise the fix",
        text: "Try a Parsons-style challenge: a function skeleton with the key logic missing, checked against edge cases.",
      },
    ],
    featuresHeading: "Built to teach debugging, not just patch your code",
    features: [
      {
        title: "Exact-line bug finder",
        body: "Off-by-one errors, unbounded recursion, modifying a list while iterating, null or undefined dereferences, integer-division traps and reference-versus-value mutation.",
      },
      {
        title: "A failing test case every time",
        body: "Input, expected output and actual output, so you can see the bug rather than take it on trust.",
      },
      {
        title: "Execution trace with a live memory table",
        body: "Follow variables like low, high and mid, plus call-stack frames, one step at a time.",
      },
      {
        title: "Predict-the-next-step prompts",
        body: "At critical branches the lab asks what happens next, training the mental model you need in exams and interviews.",
      },
      {
        title: "Parsons-style scaffolding",
        body: "Fill in the missing core logic of a clean skeleton and get a pass/fail matrix on edge cases.",
      },
      {
        title: "Big-O and a rule of thumb",
        body: "Time and space complexity for the code, plus a short exam-ready rule to avoid the same bug again.",
      },
    ],
    example: {
      title: "Example: an off-by-one in binary search",
      intro: "This binary search looks fine but crashes on some inputs.",
      blocks: [
        {
          label: "Student code",
          code:
            "def binary_search(arr, target):\n" +
            "    low, high = 0, len(arr)          # <- faulty line: should be len(arr) - 1\n" +
            "    while low <= high:\n" +
            "        mid = (low + high) // 2\n" +
            "        if arr[mid] == target:\n" +
            "            return mid\n" +
            "        elif arr[mid] < target:\n" +
            "            low = mid + 1\n" +
            "        else:\n" +
            "            high = mid - 1\n" +
            "    return -1",
        },
        {
          label: "Failing test case",
          code:
            "Input:    arr = [1, 3], target = 5\n" +
            "Expected: -1\n" +
            "Actual:   IndexError: list index out of range",
        },
        {
          label: "Why it breaks",
          text: "high starts one past the last valid index. With target 5, low becomes 2 and mid becomes 2, so arr[2] reads outside the list.",
        },
        {
          label: "Fix and complexity",
          code: "low, high = 0, len(arr) - 1      # Time O(log n), Space O(1)",
        },
      ],
      takeaway:
        "Defect type: off-by-one boundary error. Rule of thumb: when the loop condition is low <= high, high must be the last valid index.",
    },
    faqs: [
      {
        q: "How do I debug code step by step?",
        a: "Reproduce the failure with a concrete input, then trace the variable values line by line until you find the first point where the actual state differs from what you expected. The Learnex Code Lab guides you through both parts: it pinpoints the faulty line, shows a failing test case and walks execution with a live memory table.",
      },
      {
        q: "Which programming languages does the Code Lab support?",
        a: "Python, JavaScript and TypeScript, Java, C++, Go and Rust are supported for the Find My Bug diagnostic.",
      },
      {
        q: "Does the Code Lab run my code?",
        // CONFIRM [2]: replace this answer if the trace runs real code in a sandbox.
        a: "The trace is an AI-guided walkthrough built for learning. It is not a replacement for your compiler or debugger, so use it to understand what is going on, then confirm the behaviour in your own environment before you submit.",
      },
      {
        q: "How is this different from asking a chatbot to fix my code?",
        a: "A fixer hands back rewritten code. The Code Lab is built to teach: it locates the line, names the defect type, proves the problem with a failing case, and then asks you to predict and rewrite the logic yourself.",
      },
      {
        q: "What is a Parsons problem?",
        a: "A Parsons problem gives you a working structure with the key logic left out, so you practise writing the part that matters. In the Code Lab your answer is checked against edge cases and you see which tests pass and fail.",
      },
      {
        q: "Is it okay to use this for assignments?",
        a: "The Code Lab is designed to help you understand your own mistakes, not to write assignments for you. Check your course's rules on AI assistance before using any tool on graded work.",
      },
    ],
    related: [
      { label: "All Learnex Labs", path: "/labs" },
      { label: "Math Mistake Finder", path: "/math-mistake-finder" },
      { label: "Biology Pathway Simulator", path: "/biology-pathway-simulator" },
      { label: "Socratic AI Tutor", path: "/socratic-tutor" },
    ],
    targetQueries: [
      "find the bug in my code and explain why",
      "trace code execution step by step",
      "code tracing practice for exams",
      "off by one error explained",
      "parsons problems practice",
    ],
    dateModified: "2026-11-04",
  },

  // ---------------------------------------------------------------- BIO
  bio: {
    key: "bio",
    path: "/biology-pathway-simulator",
    crumbName: "Biology Pathway Simulator",
    title: "Biology Pathway Simulator: What Breaks If...? | Learnex",
    description:
      "See what breaks when a drug, toxin or mutation blocks a pathway: upstream buildup, downstream depletion and compensation, step by step.",
    h1: "What breaks if...? See the whole cascade, step by step",
    subhead:
      "Pick a drug, toxin, enzyme inhibitor or mutation. Learnex traces what builds up before the block, what runs dry after it and how the body compensates.",
    ctaLabel: "Start free",
    ctaPath: "/signup",
    quickAnswer:
      "If a drug or mutation blocks one enzyme in a pathway, the substrate before the block builds up (upstream accumulation) and the products after it fall (downstream depletion). The cell may then compensate through feedback regulation or alternative routes. The Learnex Bio Lab maps that whole cascade, stage by stage, for any drug, toxin or mutation you choose.",
    howToName: "How to predict what happens when a biological pathway is blocked",
    steps: [
      {
        name: "Choose the perturbation",
        text: "Enter a drug, toxin, enzyme inhibitor or mutation, for example a statin, cyanide, an ACE inhibitor or an insulin receptor defect.",
      },
      {
        name: "Find the direct target",
        text: "Learnex names the molecular target, such as an enzyme, channel or receptor, and where in the cell it acts.",
      },
      {
        name: "Follow the cascade",
        text: "Each stage is labelled Normal, Inhibited, Accumulating, Depleted or Compensating, with its location, inputs and outputs.",
      },
      {
        name: "Test your reasoning",
        text: "Answer a board-style challenge question about the causal chain and get feedback on your logic.",
      },
    ],
    featuresHeading: "Train causal thinking, not just recall",
    features: [
      {
        title: "Upstream vs downstream, side by side",
        body: "Paired cards show what accumulates before the block and what is depleted after it.",
      },
      {
        title: "Five clear stage labels",
        body: "Normal, Inhibited, Accumulating, Depleted and Compensating make the flow of the cascade easy to read.",
      },
      {
        title: "Causal pathway tracker",
        body: "Ordered stages with cellular locations, inputs and outputs, and the rate-limiting checkpoints that matter.",
      },
      {
        title: "Homeostatic feedback explorer",
        body: "Compare negative feedback that restores a set point with positive feedback that amplifies to a climax.",
      },
      {
        title: "Exam-style challenge questions",
        body: "MCAT-style and board-style questions that test whether you can reason through cause and effect.",
      },
    ],
    example: {
      title: "Example: what a statin blocks",
      intro:
        "A statin inhibits HMG-CoA reductase, the rate-limiting enzyme of cholesterol synthesis. Here is how the cascade reads.",
      blocks: [],
      table: {
        head: ["Stage", "Status", "Why"],
        rows: [
          ["HMG-CoA reductase", "Inhibited", "Direct target of the drug"],
          ["HMG-CoA", "Accumulating", "Upstream substrate can't be converted as fast"],
          ["Mevalonate and later intermediates, including cholesterol", "Depleted", "Downstream products are made more slowly"],
          ["LDL receptor expression in the liver", "Compensating", "Low cell cholesterol switches on SREBP-2, so liver cells take up more LDL from the blood"],
        ],
      },
      takeaway:
        "The compensation step is the exam point: the drug lowers blood LDL partly because the cell responds to its own cholesterol shortage.",
    },
    faqs: [
      {
        q: "What happens if an enzyme in a metabolic pathway is blocked?",
        a: "The substrate just before the block builds up (upstream accumulation) and the products after it fall (downstream depletion). The cell may partly compensate through feedback regulation or alternative routes. In a pathway A to B to C, blocking the enzyme that converts A to B raises A and lowers B and C.",
      },
      {
        q: "What is the difference between upstream accumulation and downstream depletion?",
        a: "Upstream accumulation is the backing-up of molecules that can no longer be processed past the block. Downstream depletion is the shortage of everything the blocked step would have made, which starves later processes. The Bio Lab shows both side by side.",
      },
      {
        q: "What does the Compensating label mean?",
        a: "Compensating marks a stage where the cell or body responds to the block, for example by changing gene expression, using an alternative pathway or adjusting a feedback loop. Statins, for instance, trigger more LDL receptors on liver cells.",
      },
      {
        q: "Is the Bio Lab useful for MCAT or board-style prep?",
        a: "It includes MCAT-style and board-style challenge questions that test causal reasoning about pathways, drugs and mutations. Use it alongside your official course and exam materials.",
      },
      {
        q: "Is this medical advice?",
        a: "No. The Bio Lab uses simplified models for education. It is not for diagnosis, treatment or any clinical decision.",
      },
      {
        q: "Which drugs, toxins and mutations can I explore?",
        a: "You can enter any drug, toxin, enzyme inhibitor or genetic mutation, for example a statin, cyanide, an ACE inhibitor or an insulin receptor defect. Results are AI-generated, so confirm details against your course materials.",
      },
    ],
    related: [
      { label: "All Learnex Labs", path: "/labs" },
      { label: "Math Mistake Finder", path: "/math-mistake-finder" },
      { label: "Code Debugging Tutor", path: "/code-debugging-tutor" },
      { label: "Socratic AI Tutor", path: "/socratic-tutor" },
    ],
    targetQueries: [
      "what happens if an enzyme is inhibited",
      "upstream accumulation downstream depletion",
      "metabolic pathway practice questions",
      "what breaks if drug blocks pathway",
      "causal pathway biology mcat",
    ],
    dateModified: "2026-11-04",
  },
};

// -------------------------------------------------------------------
// JSON-LD generated from the SAME data the page renders.
// Note: Google limits FAQ/HowTo rich results, so this schema is mainly for answer engines
// and LLM crawlers (Perplexity, ChatGPT Search, Gemini). Keep it accurate and keep it in sync.
// Deliberately contains no pricing/offers so it can't drift from the pricing page.
// -------------------------------------------------------------------
export function buildLabJsonLd(p: LabPage): object[] {
  const url = SITE + p.path;

  const crumbs = [{ name: "Home", url: SITE + "/" }];
  if (p.key !== "hub") crumbs.push({ name: "Labs", url: SITE + "/labs" });
  crumbs.push({ name: p.crumbName, url });

  const schemas: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": url + "#webpage",
      url,
      name: p.title,
      description: p.description,
      dateModified: p.dateModified,
      isPartOf: { "@type": "WebSite", name: "Learnex", url: SITE },
      about: {
        "@type": "SoftwareApplication",
        name: "Learnex",
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        url: SITE,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.url,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: p.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  if (p.steps.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: p.howToName,
      step: p.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  return schemas;
}